import React from 'react';
import { useIntl } from "react-intl";
import { useAtomValue } from "jotai";
import classNames from "classnames";
import selectorRepository from "@/repository/selectorRepository";
import { usePaginatedSelectors } from "@/query/selector";
import { singleTestPanelOpenAtom } from "@/atom/singleTestPanel";
import { useAgentId, useModal, usePageParam } from "@/hook";
import Button from "@/component/Button";
import { SearchBar } from "@/component/input";
import { UploadModal } from "@/component/modal";
import PaginationBar from "@/component/PaginationBar";
import { SingleTestPanel, SingleTestButton } from '@/component/singleTest';
import { DownloadIcon, UploadIcon } from "@/assets/svgs/icons";

import SelectorForm from "./component/SelectorForm";
import SelectorTable from "./component/SelectorTable";


const SELECTOR_PAGE_SIZE = 15;
const SELECTOR_UPLOAD_MODAL_ID = "selector-upload-modal";

type SortKey = 'sentence' | 'intent_name';

export interface SelectorPageParam {
    page: number;
    size: number;
    searchKeyword: string;
    sortKey: SortKey;
    sortOrder: 'asc' | 'desc';
}

const SelectorView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();

    const { param, set: setParam } = usePageParam<SelectorPageParam>({
        page: 0,
        size: SELECTOR_PAGE_SIZE,
        searchKeyword: "",
        sortKey: "sentence",
        sortOrder: "asc",
    });

    const singleTestPanelOpen = useAtomValue(singleTestPanelOpenAtom);
    const { data: selectorPage } = usePaginatedSelectors(
        agentId,
        param?.page,
        param?.size,
        param?.searchKeyword || undefined,
        param?.sortKey,
        param?.sortOrder,
    );

    const onClickUpload = async (file: File, isCleanImport: boolean) => {
        await selectorRepository.upload(agentId, file, isCleanImport);
    };

    const onChangeSort = (key: SortKey) => {
        if (param?.sortKey === key) {
            setParam({
                sortOrder: param.sortOrder === "desc" ? "asc" : "desc",
            });
        } else {
            setParam({
                sortKey: key,
                sortOrder: "desc",
            });
        }
    };

    return (<>
        <UploadModal id={SELECTOR_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i04002" })}
                     cleanImportText={intl.formatMessage({ id: "i04023" })}
                     sampleFileName="nlu_selector_sample"
                     sampleFileColumns={[
                         { attribute: "text", description: intl.formatMessage({ id: "i04003" }) },
                         { attribute: "intent_name", description: intl.formatMessage({ id: "i04004" }) },
                     ]}
                     upload={onClickUpload}
        />

            <div className={classNames(
                     "agent-content-layout",
                     singleTestPanelOpen && "test-panel-open"
                 )}
            >
                <div className={classNames(
                         "full-page-container flex flex-col gap-3",
                         singleTestPanelOpen && "test-panel-open"
                     )}
                >
                <div className="search-bar-container">
                    <SearchBar lg width={680}
                               placeholder={intl.formatMessage({ id: "i04015" })}
                               onSearch={query => setParam({ searchKeyword: query })}
                    />

                    <div className="search-bar-container-btn-group">
                        <Button variant="text"
                                onClick={() => modal.open(SELECTOR_UPLOAD_MODAL_ID)}
                        >
                            <UploadIcon className="size-3.5" />
                            {intl.formatMessage({ id: "b0004" })}
                        </Button>

                        <Button variant="text"
                                onClick={() => selectorRepository.export(agentId)}
                        >
                            <DownloadIcon className="size-3.5" />
                            {intl.formatMessage({ id: "b0005" })}
                        </Button>

                        <SingleTestButton />
                    </div>
                </div>

                <div className="form-table-container">
                    <SelectorForm />
                    <div className="table-pagination-container">
                        <SelectorTable page={selectorPage}
                                       pageParam={param}
                                       onClickSortHead={onChangeSort}
                        />
                        <PaginationBar pageData={selectorPage}
                                       onClickPage={page => setParam({ page })}
                        />
                    </div>

                    <SingleTestPanel className="ml-auto" />
                </div>
            </div>
        </div>
    </>);
};

export default SelectorView;
